import {
  GraphQLFieldConfig,
  GraphQLNonNull,
  GraphQLList,
  GraphQLID,
  GraphQLString,
  GraphQLInputObjectType,
  GraphQLObjectType
} from 'graphql'
import { Context } from '../shared'
import {
  AgentLanguageBucket,
  AgentLanguageBucketType,
  CustomMetadataBuckets,
  CustomMetadataBucketsType,
  ServerBucket,
  ServerBucketType,
  ServerEnvironmentBucket,
  ServerEnvironmentBucketType,
  TermBucket,
  TermBucketType
} from '../types/bucket'
import {
  extractBuckets,
  filterGroupsToBuckets,
  mapLabeledBucket,
  mapServerBucket,
  mapTermBucket
} from '../endpoint-helpers/applications'
import {
  CustomMetadataFilter,
  customMetadataFilter
} from '../types/application-filter-arg'

export interface ArchitectureAssociatedApplicationFilterArg {
  readonly apps: string[]
  readonly languages?: string[]
  readonly technologies?: string[]
  readonly servers?: string[]
  readonly tags?: string[]
  readonly environments?: string[]
  readonly customMetadata?: CustomMetadataFilter[]
}

const filterList = new GraphQLList(GraphQLString)

export const architectureAssociatedApplicationFilterArg =
  new GraphQLInputObjectType({
    name: 'ArchitectureAssociatedApplicationFilterArg',
    description:
      'A collection of criteria categories and values to filter the architecture associated applications by. Each criteria category consists of a list of strings. Categories of criteria are applied with an AND relationship. Within each category, the individual criteria values are applied with an OR relationaship',
    fields: () => ({
      apps: {
        description:
          'A list of applications IDs representing the scope in which the filters are gonna be applied for',
        type: filterList
      },
      languages: {
        description: 'Filters applications by language',
        type: filterList
      },
      technologies: {
        description: 'Filters applications by technology name',
        type: filterList
      },
      servers: {
        description: 'Filters applications by server ID',
        type: filterList
      },
      tags: {
        description: 'Filters applications by tags',
        type: filterList
      },
      environments: {
        description: 'Filters applications by server environments name',
        type: filterList
      },
      customMetadata: {
        description: 'Filter by custom metadata fields',
        type: new GraphQLList(customMetadataFilter)
      }
    })
  })

export interface ArchitectureAssociatedApplicationAggregationArgs {
  readonly organizationID: string
  readonly applicationID: string
  readonly filters?: ArchitectureAssociatedApplicationFilterArg
}

export const architectureAssociatedApplicationAggregationArgs = {
  organizationID: {
    description: 'The unique ID for the organization being queried',
    type: new GraphQLNonNull(GraphQLID)
  },
  applicationID: {
    description: 'The unique ID for the application being queried',
    type: new GraphQLNonNull(GraphQLID)
  },
  filters: {
    description: 'A collection of filters to apply to the query',
    type: architectureAssociatedApplicationFilterArg
  }
}

export interface ArchitectureAssociatedApplicationAggregation {
  readonly languages: AgentLanguageBucket[]
  readonly technologies: TermBucket[]
  readonly servers: ServerBucket[]
  readonly tags: TermBucket[]
  readonly environments: ServerEnvironmentBucket[]
}

export const architectureAssociatedApplicationAggregation =
  new GraphQLObjectType({
    name: 'ArchitectureAssociatedApplicationAggregation',
    description:
      'The categories of buckets and their values that together make up an aggregation of a collection of architecture associated applications known to Contrast.',
    fields: () => ({
      languages: {
        type: new GraphQLNonNull(new GraphQLList(AgentLanguageBucketType))
      },
      technologies: {
        type: new GraphQLNonNull(new GraphQLList(TermBucketType))
      },
      servers: {
        type: new GraphQLNonNull(new GraphQLList(ServerBucketType))
      },
      tags: {
        type: new GraphQLNonNull(new GraphQLList(TermBucketType))
      },
      environments: {
        type: new GraphQLNonNull(new GraphQLList(ServerEnvironmentBucketType))
      }
    })
  })

export function mapArchitectureAssociatedAppsFilters(
  filters: ArchitectureAssociatedApplicationFilterArg
) {
  const {
    apps = [],
    languages = [],
    technologies = [],
    servers = [],
    tags = [],
    environments = [],
    customMetadata
  } = filters

  return {
    filterApps: apps,
    filterTechs: technologies,
    filterLanguages: languages,
    filterTags: tags,
    filterServers: servers,
    filterEnvironments: environments,
    metadataFilters: customMetadata
  }
}

export function appendFiltersToBody(
  filters: ArchitectureAssociatedApplicationFilterArg | undefined
) {
  if (filters) {
    const {
      filterLanguages,
      filterTechs,
      filterServers,
      filterTags,
      filterEnvironments,
      filterApps,
      metadataFilters
    } = mapArchitectureAssociatedAppsFilters(filters)

    let body: any = {
      filterApps,
      filterEnvironments,
      filterLanguages,
      filterTechs,
      filterServers,
      filterTags
    }

    if (metadataFilters) {
      body = {
        ...body,
        metadataFilters
      }
    }

    return body
  }

  return {}
}

export function mapServerEnvironmentBucket({ keycode: id, label, count }) {
  return {
    count,
    key: {
      id,
      label
    }
  }
}

// TS-6892
const maxBucketLengthSupported = 10000

// TS-6892
export function extractBucketsIfLengthIsSupported(
  mapper,
  maxBucketLengthSupported
) {
  return function bucketMapper(data) {
    let filters = data.filters

    if (Array.isArray(filters) && filters.length > maxBucketLengthSupported) {
      filters = []
    }

    return filters.map(mapper)
  }
}

const extractLanguageBuckets = extractBuckets(mapLabeledBucket)
const extractTermBuckets = extractBuckets(mapTermBucket)
const extractServerBuckets = extractBucketsIfLengthIsSupported(
  mapServerBucket,
  maxBucketLengthSupported
) // TS-6892
const extractServerEnvironmentBuckets = extractBuckets(
  mapServerEnvironmentBucket
)

type ArchitectureAssociatedApplicationBucket =
  | 'languages'
  | 'technologies'
  | 'servers'
  | 'tags'
  | 'server-environments'

export const architectureAssociatedApplicationAggregations: GraphQLFieldConfig<
  any,
  Context,
  ArchitectureAssociatedApplicationAggregationArgs
> = {
  description:
    'Get the aggregation for a collection of architecture associated applications',
  type: new GraphQLNonNull(architectureAssociatedApplicationAggregation),
  args: architectureAssociatedApplicationAggregationArgs,
  async resolve(
    _source,
    args,
    context
  ): Promise<ArchitectureAssociatedApplicationAggregation> {
    const { organizationID, applicationID, filters } = args

    const mappedFilters = appendFiltersToBody(filters)

    const getAggregation = (
      bucketType: ArchitectureAssociatedApplicationBucket
    ) =>
      context.connector.post(
        `/${organizationID}/flowmap/${applicationID}/filters/${bucketType}`,
        mappedFilters
      )

    const [languages, technologies, servers, tags, environments] =
      await Promise.all([
        getAggregation('languages').then(extractLanguageBuckets),
        getAggregation('technologies').then(extractTermBuckets),
        getAggregation('servers').then(extractServerBuckets),
        getAggregation('tags').then(extractTermBuckets),
        getAggregation('server-environments').then(
          extractServerEnvironmentBuckets
        )
      ])

    return { languages, technologies, servers, tags, environments }
  }
}

export const customArchitectureAssociatedApplicationAggregations: GraphQLFieldConfig<
  any,
  Context,
  ArchitectureAssociatedApplicationAggregationArgs
> = {
  description:
    'Get the aggregations of custom metadata values for a collection of architecture associated applications',
  type: new GraphQLNonNull(new GraphQLList(CustomMetadataBucketsType)),
  args: architectureAssociatedApplicationAggregationArgs,
  async resolve(_source, args, context): Promise<CustomMetadataBuckets[]> {
    const { organizationID, applicationID, filters } = args

    const mappedFilters = appendFiltersToBody(filters)

    const { filters: metadataFilters } = await context.connector.post(
      `/${organizationID}/flowmap/${applicationID}/metadata`,
      mappedFilters
    )

    return filterGroupsToBuckets(metadataFilters)
  }
}
